/**
 * Anonymous guest session client. A QR scanner joins a mission without logging
 * in: the server issues a token that this module persists in localStorage
 * (keyed by join code) and reuses across reloads until it expires or the leader
 * removes the guest.
 */

import type { TakShape } from '@vtak/shared';
import { CLIENT_CONFIG } from '@/config';

/** A guest session as returned by the public guest API. */
export interface GuestSession {
  joinCode: string;
  guestSub: string;
  guestToken: string;
  status: string;
  audioGranted: boolean;
  viewGranted: boolean;
  expiresAt: string;
}

/** localStorage key for a code's guest token. */
function tokenKey(joinCode: string): string {
  return `vtak.guest.${joinCode}`;
}

/** The stored guest token for a code, if any. */
export function storedGuestToken(joinCode: string): string | null {
  return window.localStorage.getItem(tokenKey(joinCode));
}

/** Forget a code's guest token (on expiry / removal). */
export function clearGuestToken(joinCode: string): void {
  window.localStorage.removeItem(tokenKey(joinCode));
}

/** The guest join code in the URL (/join?code=… or ?guest=…), or null. */
export function guestCodeFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const guest = params.get('guest');
  if (guest) return guest;
  if (window.location.pathname === '/join') return params.get('code');
  return null;
}

/** Start a new anonymous guest session for a mission code (persists the token). */
export async function joinAsGuest(joinCode: string, name: string): Promise<GuestSession | null> {
  const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/guest/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ joinCode, name }),
  });
  if (!res.ok) return null;
  const session = (await res.json()) as GuestSession;
  window.localStorage.setItem(tokenKey(joinCode), session.guestToken);
  return session;
}

/**
 * Start a no-login, view-only guest session from a unit-invite code. The server
 * canonicalises the code into the session's `joinCode`, under which the token is
 * persisted — so a later resume keys on that same value.
 */
export async function joinAsInviteGuest(code: string, name: string): Promise<GuestSession | null> {
  const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/guest/join-invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, name }),
  });
  if (!res.ok) return null;
  const session = (await res.json()) as GuestSession;
  window.localStorage.setItem(tokenKey(session.joinCode), session.guestToken);
  return session;
}

/** Poll the current guest session state; null when expired/removed. */
export async function fetchGuestSession(token: string): Promise<GuestSession | null> {
  const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/guest/session`, {
    headers: { 'X-Guest-Token': token },
  });
  if (!res.ok) return null;
  return (await res.json()) as GuestSession;
}

/** The unit a unit-invite code belongs to (name only), or null when invalid. */
export async function fetchInviteUnit(code: string): Promise<{ unitName: string } | null> {
  const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/guest/invite/${encodeURIComponent(code)}`);
  if (!res.ok) return null;
  return (await res.json()) as { unitName: string };
}

/** The mission-area outline shapes a view-granted guest may see; [] otherwise. */
export async function fetchGuestMissionArea(token: string): Promise<TakShape[]> {
  const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/guest/mission-area`, {
    headers: { 'X-Guest-Token': token },
  });
  if (!res.ok) return [];
  return (await res.json()) as TakShape[];
}

/** The mission's channels (id + name) an audio-granted guest may listen to. */
export async function fetchGuestChannels(token: string): Promise<Array<{ id: number; name: string }>> {
  const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/guest/channels`, {
    headers: { 'X-Guest-Token': token },
  });
  if (!res.ok) return [];
  return (await res.json()) as Array<{ id: number; name: string }>;
}

/** A listen-only LiveKit token for an audio-granted guest, or null. */
export async function fetchGuestListenToken(
  token: string,
  channelId: number,
): Promise<{ token: string; wsUrl: string } | null> {
  const res = await fetch(`${CLIENT_CONFIG.apiBase}/api/guest/channel/${channelId}/listen`, {
    method: 'POST',
    headers: { 'X-Guest-Token': token },
  });
  if (!res.ok) return null;
  return (await res.json()) as { token: string; wsUrl: string };
}
